import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  KeyRound,
  Plus,
  Copy,
  Check,
  Trash2,
  Clock,
  Eye,
  ShieldAlert,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  createEmergencyGrant,
  listEmergencyGrants,
  revokeEmergencyGrant,
  fetchReleaseWindow,
  setReleaseWindow,
} from "../lib/api";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { tr } from "../lib/i18n";

// Owner-chosen waiting period for executor access requests (medical "right away" tier is unaffected).
const ReleaseWindowRow = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["release-window"], queryFn: fetchReleaseWindow });
  const save = useMutation({ mutationFn: setReleaseWindow, onSuccess: (r) => { qc.setQueryData(["release-window"], (p) => ({ ...(p || {}), days: r.days, set: true })); qc.invalidateQueries({ queryKey: ["executor-preview"] }); qc.invalidateQueries({ queryKey: ["owner-checklist"] }); toast.success(`Executors now wait ${r.days} day${r.days === 1 ? "" : "s"} unless you decline`); } });
  if (!data) return null;
  return (
    <div className="mb-4 rounded-xl border border-border bg-background/40 px-3.5 py-3 flex flex-wrap items-center gap-3" data-testid="release-window-row">
      <div className="flex-1 min-w-[12rem]"><div className="text-sm font-medium">{tr("Waiting period for access requests")}</div><div className="text-xs text-muted-foreground">{tr("When an executor asks for access you're emailed and can decline. If you don't, access is released after this many days. Documents marked “opens to a verified executor right away” skip the wait.")}</div></div>
      <Select value={String(data.days)} onValueChange={(v) => save.mutate(Number(v))}>
        <SelectTrigger className="h-9 w-36" data-testid="release-window-select"><SelectValue /></SelectTrigger>
        <SelectContent>{data.choices.map((d) => <SelectItem key={d} value={String(d)} data-testid={`release-window-${d}`}>{d === 1 ? "1 day (24 h)" : d === 2 ? "2 days (48 h)" : d === 3 ? "3 days (72 h)" : `${d} days`}</SelectItem>)}</SelectContent>
      </Select>
      {data.set === false && <Button size="sm" variant="outline" className="h-9 rounded-full" onClick={() => save.mutate(data.days)} data-testid="release-window-confirm">{tr("Keep")} {data.days} {tr("days")}</Button>}
    </div>
  );
};

const DAYS = [
  { v: "7", l: "7 days" },
  { v: "30", l: "30 days" },
  { v: "90", l: "90 days" },
  { v: "365", l: "1 year" },
];

export const EmergencyAccessSection = () => {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [notifyEmail, setNotifyEmail] = useState(
    localStorage.getItem("hv_reminder_email") || ""
  );
  const [days, setDays] = useState("30");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // {code, link}
  const [copied, setCopied] = useState("");

  const { data: grants = [] } = useQuery({
    queryKey: ["emergency-grants"],
    queryFn: listEmergencyGrants,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["emergency-grants"] });

  const handleCreate = async () => {
    if (!name.trim()) return toast.error("Add a recipient name");
    setBusy(true);
    try {
      const res = await createEmergencyGrant({
        recipient_name: name,
        recipient_email: recipientEmail,
        owner_notify_email: notifyEmail,
        days_valid: Number(days),
      });
      const link = `${window.location.origin}${res.share_path}`;
      setCreated({ code: res.code, link });
      toast.success("Emergency access created");
      setName("");
      setRecipientEmail("");
      refresh();
    } catch (e) {
      toast.error("Could not create access link");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, tag) => {
    navigator.clipboard?.writeText(text);
    setCopied(tag);
    toast.success("Copied");
    setTimeout(() => setCopied(""), 1500);
  };

  const revoke = async (id) => {
    try {
      await revokeEmergencyGrant(id);
      toast.success("Access revoked");
      refresh();
    } catch {
      toast.error("Could not revoke");
    }
  };

  const statusBadge = (g) => {
    if (g.revoked) return { l: "Revoked", cls: "bg-stone-200 text-stone-600 dark:bg-stone-700 dark:text-stone-300" };
    if (g.expired) return { l: "Expired", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" };
    return { l: "Active", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" };
  };

  return (
    <motion.section
      data-testid="section-emergency"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-card border border-border rounded-2xl p-5 sm:p-6 lg:col-span-2"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-serif text-xl font-medium">{tr("Emergency Access")}</h3>
          <p className="text-sm text-muted-foreground">
            {tr("Create a time-locked \"break-glass\" link a trusted person can open with a code. You're notified the moment it's used.")}
          </p>
        </div>
      </div>
      <ReleaseWindowRow />

      {/* Create form */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Input
          data-testid="emergency-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={tr("Recipient name (e.g. Jane, my executor)")}
          className="bg-background/40"
        />
        <Input
          data-testid="emergency-recipient-email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder={tr("Recipient email (optional)")}
          className="bg-background/40"
        />
        <Input
          data-testid="emergency-notify-email"
          value={notifyEmail}
          onChange={(e) => setNotifyEmail(e.target.value)}
          placeholder={tr("Notify me at (your email)")}
          className="bg-background/40"
        />
        <div className="flex gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger data-testid="emergency-days-select" className="bg-background/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAYS.map((d) => (
                <SelectItem key={d.v} value={d.v}>
                  {tr("Valid")} {d.l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="create-emergency-btn"
            onClick={handleCreate}
            disabled={busy}
            className="gap-1.5 bg-primary hover:bg-primary/90 shrink-0"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {tr("Create")}
          </Button>
        </div>
      </div>

      {/* Created code (shown once) */}
      {created && (
        <div
          data-testid="emergency-created-box"
          className="mt-4 rounded-xl border border-primary/40 bg-primary/5 p-4"
        >
          <div className="flex items-center gap-2 text-primary text-sm font-medium mb-3">
            <ShieldAlert className="h-4 w-4" /> {tr("Share these privately — the code is shown only once")}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-lg bg-card border border-border px-3 py-2">
              <span className="text-[11px] uppercase font-mono text-muted-foreground">{tr("Link")}</span>
              <span className="text-xs font-mono truncate flex-1">{created.link}</span>
              <button onClick={() => copy(created.link, "link")}>
                {copied === "link" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-card border border-border px-3 py-2">
              <span className="text-[11px] uppercase font-mono text-muted-foreground">{tr("Code")}</span>
              <span className="text-lg font-mono font-semibold tracking-[0.3em] flex-1">{created.code}</span>
              <button onClick={() => copy(created.code, "code")}>
                {copied === "code" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing grants */}
      {grants.length > 0 && (
        <div className="mt-5 space-y-2">
          {grants.map((g) => {
            const s = statusBadge(g);
            return (
              <div
                key={g.id}
                data-testid="emergency-grant-item"
                className="flex items-center gap-3 rounded-xl border border-border bg-background/40 px-3.5 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm truncate">{g.recipient_name}</span>
                    <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-full ${s.cls}`}>
                      {s.l}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {tr("Expires")} {new Date(g.expires_at).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" /> {g.access_count} {tr("view")}{g.access_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                {!g.revoked && (
                  <Button
                    data-testid="revoke-emergency-btn"
                    variant="ghost"
                    size="sm"
                    onClick={() => revoke(g.id)}
                    className="text-destructive hover:text-destructive gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {tr("Revoke")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.section>
  );
};
